// src/config/constants.ts

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL,
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

export const API_DEVICES_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL_DEVICES,
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

/**
 * Authentication Configuration
 */
export const AUTH_CONFIG = {
  TOKEN_KEY: 'token',
  REFRESH_TOKEN_KEY: 'refresh_token',
  USER_KEY: 'user',
  ROLE_KEY: 'role',
  TOKEN_EXPIRATION_KEY: 'token_expiration',
  AUTH_METHOD_KEY: 'auth_method',
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours in ms
  REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiring
} as const;

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 1000,
} as const;

/**
 * Device Types
 */
export const DEVICE_TYPES = {
  KIOSK: 'Kiosk',
  DISPENSER: 'Dispenser',
  ONLINE_CHECKIN: 'OnlineCheckIn',
  MOBILE_KIOSK: 'MobileKiosk',
  OTHER: 'Other',
} as const;

/**
 * Device Status
 */
export const DEVICE_STATUS = {
  ONLINE: 'Online',
  OFFLINE: 'Offline',
  ERROR: 'Error',
  MAINTENANCE: 'Maintenance',
} as const;

/**
 * Channels
 */
export const CHANNELS = {
  ONLINE: 'online',
  KIOSK: 'kiosk',
  RECEPTIONIST: 'receptionist',
  MOBILE_KIOSK: 'mobileKiosk',
  DISPENSER: 'dispenser',
} as const;

/**
 * Check-in Types
 */
export const CHECKIN_TYPES = {
  RESERVATION: 'Reservation',
  WALKING: 'Walking',
} as const;

/**
 * Event Codes
 */
export const EVENT_CODES = {
  PC_LINK_OPEN: 'PC_LINK_OPEN', // Pre-checkin link opened
  PC_START: 'PC_START',       // Pre-checkin started
  PC_COMP: 'PC_COMP',         // Pre-checkin completed
  CI_START: 'CI_START',       // Check-in started
  CI_COMP: 'CI_COMP',         // Check-in completed
  PM_SUCCESS: 'PM_SUCCESS',   // Payment successful
  PM_ERROR: 'PM_ERROR',       // Payment error
  KD_SUCCESS: 'KD_SUCCESS',   // Key dispensed successfully
  KD_ERROR: 'KD_ERROR',       // Key dispense error
  ERR: 'ERR',                 // Generic error
} as const;

/**
 * Section Codes
 */
export const SECTION_CODES = {
  S: 'S',   // Start
  I: 'I',   // Identification
  F: 'F',   // Form
  U: 'U',   // Upsell
  P: 'P',   // Payment
  K: 'K',   // Key
} as const;

/**
 * Alert Types
 */
export const ALERT_TYPES = {
  DEVICE_OFFLINE: 'DeviceOffline',
  HIGH_ERROR_RATE: 'HighErrorRate',
  LOW_COMPLETION_RATE: 'LowCompletionRate',
  SYSTEM_ERROR: 'SystemError',
} as const;

/**
 * Alert Severity Levels
 */
export const ALERT_SEVERITY = {
  INFO: 'Info',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
} as const;

/**
 * Alert Status
 */
export const ALERT_STATUS = {
  ACTIVE: 'Active',
  ACKNOWLEDGED: 'Acknowledged',
  RESOLVED: 'Resolved',
} as const;

/**
 * Date Range Presets for Dashboard
 */
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last7days',
  LAST_30_DAYS: 'last30days',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  CUSTOM: 'custom',
} as const;

/**
 * Chart Colors for Dashboard
 */
export const CHART_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#dc004e',
  SUCCESS: '#4caf50',
  WARNING: '#ff9800',
  ERROR: '#f44336',
  INFO: '#2196f3',
  ONLINE: '#00c853',
  KIOSK: '#1976d2',
  RECEPTIONIST: '#9c27b0',
  MOBILE_KIOSK: '#ff9800',
  DISPENSER: '#673ab7',
} as const;

/**
 * Default Language Options
 */
export const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
] as const;

/**
 * User Roles
 */
export const USER_ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  GROUP_ADMIN: 'GroupAdmin',
  HOTEL_ADMIN: 'HotelAdmin',
  RECEPTIONIST: 'Receptionist',
} as const;

/**
 * Analytics Configuration
 */
export const ANALYTICS_CONFIG = {
  // Refresh intervals
  DASHBOARD_REFRESH_INTERVAL: 60000, // 1 minute
  DEVICE_STATUS_REFRESH_INTERVAL: 30000, // 30 seconds
  ALERT_CHECK_INTERVAL: 60000, // 1 minute
  
  // Thresholds
  DEVICE_OFFLINE_THRESHOLD_MINUTES: 15,
  HIGH_ERROR_RATE_THRESHOLD_PERCENT: 10,
  LOW_COMPLETION_RATE_THRESHOLD_PERCENT: 50,
  
  // Data retention
  EVENT_RETENTION_DAYS: 30,
  CHECKIN_RETENTION_MONTHS: 6,
  JOURNEY_RETENTION_MONTHS: 6,
  ANALYTICS_RETENTION_MONTHS: 12,
} as const;

/**
 * Export Configuration
 */
export const EXPORT_CONFIG = {
  MAX_EXPORT_ROWS: 10000,
  EXPORT_FORMATS: ['xlsx', 'csv', 'pdf'] as const,
  DEFAULT_FORMAT: 'xlsx',
} as const;

/**
 * Application Environment
 */
export const APP_ENV = {
  NODE_ENV: import.meta.env.MODE || 'development',
  IS_PRODUCTION: import.meta.env.PROD,
  IS_DEVELOPMENT: import.meta.env.DEV,
  LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL || 'debug',
  MOCK_API: import.meta.env.VITE_MOCK_API === 'true',
} as const;

// Derived types from constants
export type DeviceType = typeof DEVICE_TYPES[keyof typeof DEVICE_TYPES];
export type DeviceStatusType = typeof DEVICE_STATUS[keyof typeof DEVICE_STATUS];
export type ChannelType = typeof CHANNELS[keyof typeof CHANNELS];
export type CheckInTypeType = typeof CHECKIN_TYPES[keyof typeof CHECKIN_TYPES];
export type EventCodeType = typeof EVENT_CODES[keyof typeof EVENT_CODES];
export type SectionCodeType = typeof SECTION_CODES[keyof typeof SECTION_CODES];
export type AlertTypeType = typeof ALERT_TYPES[keyof typeof ALERT_TYPES];
export type AlertSeverityType = typeof ALERT_SEVERITY[keyof typeof ALERT_SEVERITY];
export type AlertStatusType = typeof ALERT_STATUS[keyof typeof ALERT_STATUS];
export type DateRangePresetType = typeof DATE_RANGE_PRESETS[keyof typeof DATE_RANGE_PRESETS];

// Helpers to get values from constants
export const getDeviceTypeValues = (): string[] => Object.values(DEVICE_TYPES);
export const getDeviceStatusValues = (): string[] => Object.values(DEVICE_STATUS);
export const getChannelValues = (): string[] => Object.values(CHANNELS);
export const getCheckInTypeValues = (): string[] => Object.values(CHECKIN_TYPES);
export const getEventCodeValues = (): string[] => Object.values(EVENT_CODES);
export const getAlertTypeValues = (): string[] => Object.values(ALERT_TYPES);
export const getAlertSeverityValues = (): string[] => Object.values(ALERT_SEVERITY);
export const getAlertStatusValues = (): string[] => Object.values(ALERT_STATUS);

// Validators based on constants
export const isValidDeviceType = (type: string): type is DeviceType => {
  return getDeviceTypeValues().includes(type);
};

export const isValidDeviceStatus = (status: string): status is DeviceStatusType => {
  return getDeviceStatusValues().includes(status);
};

export const isValidChannel = (channel: string): channel is ChannelType => {
  return getChannelValues().includes(channel);
};

export const isValidCheckInType = (type: string): type is CheckInTypeType => {
  return getCheckInTypeValues().includes(type);
};

export const isValidEventCode = (code: string): code is EventCodeType => {
  return getEventCodeValues().includes(code);
};

export const isValidAlertType = (type: string): type is AlertTypeType => {
  return getAlertTypeValues().includes(type);
};

export const isValidAlertSeverity = (severity: string): severity is AlertSeverityType => {
  return getAlertSeverityValues().includes(severity);
};

export const isValidAlertStatus = (status: string): status is AlertStatusType => {
  return getAlertStatusValues().includes(status);
};